"use client";

import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";

import { cn } from "@/lib/utils/cn";

interface TourPageExpandableTextProps {
  text: string;
  collapsedLabel: string;
  className?: string;
  buttonClassName?: string;
  ariaLabelContext?: string;
  lines?: 2 | 3;
}

export default function TourPageExpandableText({
  text,
  collapsedLabel,
  className,
  buttonClassName,
  ariaLabelContext,
  lines = 3,
}: TourPageExpandableTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = useId();
  const buttonLabel = isExpanded ? "Show less" : collapsedLabel;

  return (
    <div>
      <p
        id={contentId}
        data-tour-story-expanded={isExpanded ? "" : undefined}
        className={cn(
          "text-[15px] leading-6 md:text-base",
          !isExpanded &&
            (lines === 2
              ? "[display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden"
              : "[display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:3] overflow-hidden"),
          className,
        )}
      >
        {text}
      </p>
      <button
        type="button"
        data-tour-story-trigger
        aria-controls={contentId}
        aria-expanded={isExpanded}
        aria-label={
          ariaLabelContext ? `${buttonLabel} for ${ariaLabelContext}` : undefined
        }
        onClick={() => setIsExpanded((current) => !current)}
        className={cn(
          "mt-3 inline-flex min-h-11 items-center gap-2 rounded-md px-0 py-2 text-sm font-bold underline-offset-4 transition-colors hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 motion-reduce:transition-none",
          buttonClassName,
        )}
      >
        {buttonLabel}
        <ChevronDown
          aria-hidden="true"
          className={cn(
            "h-4 w-4 transition-transform motion-reduce:transition-none",
            isExpanded && "rotate-180",
          )}
        />
      </button>
    </div>
  );
}
